import type { ListBuilderConfiguration } from '../types';

/**
 * Issue Sourcing Request Embedded list configuration
 * Shows issues linked to a specific sourcing request
 * Converted from JSON to TypeScript for type safety
 */
export const ISSUE_SOURCING_REQUEST_EMBEDDED_CONFIG: ListBuilderConfiguration =
	{
		lists: [
			{
				id: 'default-issues-sourcing-request-embedded',
				cacheTTL: 600000,
				name: 'Sourcing Request Issues',
				type: 'default',
				active: true,
				sortOrder: 0,
				columns: [
					{
						id: 'issue-status',
						fields: [
							{
								id: 'status-field',
								dataConfig: {
									type: 'field',
									field: { fieldName: 'status', objectTable: 'issue' },
								},
								displayStyle: 'custom',
								customComponent: { id: 'issue-status-selector' },
							},
						],
						label: 'Status',
						headers: [{ id: 'h1', text: 'Status' }],
						styleConfig: {
							size: 'md',
							colorRule: { type: 'default' },
						},
						width: 'w-16',
					},
					{
						id: 'issue-priority',
						fields: [
							{
								id: 'priority-field',
								dataConfig: {
									type: 'field',
									field: { fieldName: 'priority', objectTable: 'issue' },
								},
								displayStyle: 'custom',
								customComponent: { id: 'issue-priority-selector' },
							},
						],
						label: 'Priority',
						headers: [{ id: 'h-priority', text: 'Priority' }],
						styleConfig: {
							size: 'md',
							colorRule: { type: 'default' },
						},
						sortable: true,
						// PD-14789: priority first, then newest. Priority alone leaves ties
						// in insertion order, so an issue created from a record landed at
						// the bottom of its list, off-screen.
						sortFields: [
							{
								direction: 'DESC',
								fieldName: 'priority',
								objectTable: 'issue',
							},
							{
								direction: 'DESC',
								fieldName: 'createdDate',
								objectTable: 'issue',
							},
						],
						width: 'w-24',
					},
					{
						id: 'issue-name-type',
						fields: [
							{
								id: 'name-field',
								link: {
									type: 'object',
									enabled: true,
									objectTable: 'issue',
								},
								dataConfig: {
									type: 'field',
									field: { fieldName: 'name', objectTable: 'issue' },
								},
								styleConfig: {
									size: 'md',
									weight: 'medium',
									colorRule: { type: 'default' },
								},
								displayStyle: 'text',
							},
							{
								id: 'line-break-1',
								dataConfig: { type: 'custom_text', customText: '' },
								styleConfig: {
									size: 'md',
									colorRule: { type: 'default' },
								},
								displayStyle: 'line_break',
							},
							{
								id: 'type-field',
								dataConfig: {
									type: 'field',
									field: { fieldName: 'type', objectTable: 'issue' },
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'theme', themeColor: 'textSecondary' },
								},
								displayStyle: 'text',
							},
						],
						label: 'Name',
						headers: [{ id: 'h2', text: 'Name' }],
						styleConfig: {
							size: 'md',
							colorRule: { type: 'default' },
						},
						width: 'w-32',
					},
					{
						id: 'issue-description',
						fields: [
							{
								id: 'description-field',
								dataConfig: {
									type: 'field',
									field: { fieldName: 'description', objectTable: 'issue' },
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'default' },
									textExpansion: { enabled: true, maxLines: 3 },
								},
								displayStyle: 'text',
							},
						],
						label: 'Description',
						headers: [{ id: 'h3', text: 'Description' }],
						styleConfig: {
							size: 'md',
							colorRule: { type: 'default' },
						},
						width: 'flex-1',
					},
					{
						id: 'issue-created-date',
						fields: [
							{
								id: 'created-date-field',
								dataConfig: {
									type: 'field',
									field: { fieldName: 'createdDate', objectTable: 'issue' },
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'default' },
								},
								displayStyle: 'date',
							},
							{
								id: 'created-line-break',
								dataConfig: { type: 'custom_text', customText: '' },
								styleConfig: {
									size: 'md',
									colorRule: { type: 'default' },
								},
								displayStyle: 'line_break',
							},
							{
								// PD-14524 — creator name shown as a subtitle beneath the
								// created date, under the single "Created" header.
								id: 'created-by-field',
								dataConfig: {
									type: 'field',
									field: { fieldName: 'creatorUserName', objectTable: 'issue' },
								},
								styleConfig: {
									size: 'sm',
									colorRule: { type: 'theme', themeColor: 'textSecondary' },
								},
								displayStyle: 'text',
							},
						],
						label: 'Created',
						headers: [{ id: 'h-created', text: 'Created' }],
						styleConfig: {
							size: 'md',
							colorRule: { type: 'default' },
						},
						width: 'w-32',
					},
				],
				defaultSortId: 'column-issue-priority-desc',
				clickAction: {
					type: 'navigate',
					path: '/issues/{id}',
				},
			},
		],
		paging: {
			defaultPageSize: 25,
			pageSizeOptions: [10, 25, 50, 100],
		},
		showResetButton: true,
		filters: {
			activeFilters: ['issueKeywords', 'issueStatus', 'issueType'],
			defaultFilters: {},
		},
		objects: [
			{
				baseUrl: '/issues/:id',
				objectLabel: 'Issue',
				objectTable: 'issue',
				selectedFields: [
					{ fieldName: '_id', fieldLabel: 'ID' },
					{ fieldName: 'name', fieldLabel: 'Name' },
					{ fieldName: 'description', fieldLabel: 'Description' },
					{ fieldName: 'status', fieldLabel: 'Status' },
					{ fieldName: 'type', fieldLabel: 'Type' },
					{ fieldName: '_created_date', fieldLabel: 'Created Date' },
					{ fieldName: '_modified_date', fieldLabel: 'Modified Date' },
					{ fieldName: 'creatorUserName', fieldLabel: 'Created By' },
				],
			},
		],
	};
